/**
 * Logic for computing prop/state-derived table measurements (heights, paddings).
 *
 * These helpers are intentionally free of DOM access so they can be memoized in
 * selectors. DOM-dependent measurements remain in the component.
 */

import {
  DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT,
  MaximumTableHeightType,
  TABLE_ROW_HEIGHT,
} from 'notebook/tables/constants/TableLayoutConstants';

/**
 * The numeric maximum height for the table. When an explicit numeric
 * `maximumHeight` is provided it is used directly; otherwise the default fixed
 * height is used. This does NOT account for whether the table is maximized or
 * intended to stretch to full height.
 */
export function computeMaxTableHeight(maximumHeight: MaximumTableHeightType | undefined): number {
  return typeof maximumHeight === 'number' ? maximumHeight : DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT;
}

/**
 * The minimum height that the `<table>` tag must be. The table may be taller
 * than this, but it should never be shorter. For a fixed-height table this is
 * capped at `maxTableHeight`; for a full-height table there is no cap.
 */
export function computeMinTableHeight(
  rowCount: number,
  columnHeaderHeight: number,
  isFullHeight: boolean,
  maxTableHeight: number
): number {
  const minFullHeight = rowCount * TABLE_ROW_HEIGHT + columnHeaderHeight;
  const effectiveMaxTableHeight = !isFullHeight ? maxTableHeight : undefined;
  if (effectiveMaxTableHeight) {
    return Math.min(minFullHeight, effectiveMaxTableHeight);
  }
  return minFullHeight;
}
